import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LayerDefinitionDto, PositionConfig, TextStyleConfig } from '../../../../../api/gen';
import { LayerType } from '../../../../../api/gen/model/layerType';
import { DisplayState } from '../../../../../api/gen/model/displayState';
import { DISPLAY_WIDGETS, DisplayWidgetDefinition, findWidget } from '../../../models/display-widgets';

/** Sentinel for the "not in the catalogue" option, which reveals the raw text field. */
const CUSTOM = '__custom__';

@Component({
    selector: 'app-layer-edit-item',
    standalone: true,
    imports: [FormsModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule, MatTooltipModule],
    templateUrl: './layer-edit-item.component.html',
    styleUrl: './layer-edit-item.component.scss'
})
export class LayerEditItemComponent {
    @Input() layer!: Partial<LayerDefinitionDto>;
    @Input() index!: number;
    @Input() layerTypes: LayerType[] = [];
    @Input() pendingImageFile: File | null = null;
    /** The state this layout belongs to — used to warn when a widget will never receive its data. */
    @Input() selectedState: DisplayState | null = null;

    @Output() layerChange = new EventEmitter<Partial<LayerDefinitionDto>>();
    @Output() remove = new EventEmitter<void>();
    @Output() imageFileSelected = new EventEmitter<File | null>();

    readonly positionFields: (keyof PositionConfig)[] = ['top', 'left', 'right', 'bottom', 'width', 'height'];

    readonly widgets = DISPLAY_WIDGETS;
    readonly CUSTOM = CUSTOM;

    isBackgroundImage(): boolean { return this.layer?.layerType === LayerType.BackgroundImage; }
    isTextOverlay(): boolean { return this.layer?.layerType === LayerType.TextOverlay; }
    isComponentWidget(): boolean { return this.layer?.layerType === LayerType.ComponentWidget; }

    // ── widget picker ────────────────────────────────────────────────────────

    /** The catalogue entry for what is currently stored, or undefined for a custom/blank value. */
    get selectedWidget(): DisplayWidgetDefinition | undefined {
        return findWidget(this.layer?.componentType);
    }

    /**
     * What the dropdown shows. An existing layer whose `componentType` predates this catalogue (or
     * is misspelt) resolves to "Custom", which keeps its value visible and editable rather than
     * silently rewriting it to something the operator did not choose.
     */
    get widgetSelection(): string {
        if (this.selectedWidget) return this.selectedWidget.componentType;
        return this.layer?.componentType ? CUSTOM : '';
    }

    onWidgetSelected(value: string): void {
        // Choosing "Custom" keeps whatever is already typed so the field can be edited, not cleared.
        if (value === CUSTOM) {
            this.emitField('componentType', this.layer?.componentType ?? '');
            return;
        }
        this.emitField('componentType', value);
    }

    /** True while the raw text field should be shown — an unrecognised value, or Custom picked. */
    get isCustomWidget(): boolean {
        return !!this.layer?.componentType && !this.selectedWidget;
    }

    /**
     * Set when this widget cannot work on the state being edited: its LiveData key is only published
     * with other states, so it would render an empty box here. The most valuable check in this form —
     * `PaymentRequiredQr` reads like the state a payment QR belongs on, and never carries the URL.
     */
    get stateMismatch(): string | null {
        const widget = this.selectedWidget;
        if (!widget || !this.selectedState) return null;
        if (widget.states.includes(this.selectedState)) return null;

        const expected = widget.states.join(', ');
        return `${widget.label} only receives ${widget.liveDataKey} on ${expected}. `
            + `On ${this.selectedState} this layer renders nothing.`;
    }

    /**
     * Set when a sized widget has no width or height **in px**, so the display falls back to its own
     * render size. Percentages count as missing on purpose: the display draws the code into a canvas
     * at a pixel size and only then fits it to the layer, so a `%` here tells it nothing. The layer
     * still fills its band either way — this costs resolution, not layout.
     */
    get missingSize(): boolean {
        if (!this.selectedWidget?.needsSize) return false;
        const isPx = (raw: string | null | undefined) => /^\s*\d+(\.\d+)?px\s*$/i.test(raw ?? '');
        return !isPx(this.layer?.position?.width) && !isPx(this.layer?.position?.height);
    }

    // ── emitters ─────────────────────────────────────────────────────────────

    emitField(field: keyof LayerDefinitionDto, value: unknown): void {
        this.layerChange.emit({ ...this.layer, [field]: value });
    }

    emitPosition(field: keyof PositionConfig, value: string): void {
        this.layerChange.emit({ ...this.layer, position: { ...this.layer.position, [field]: value || null } });
    }

    emitStyle(field: keyof TextStyleConfig, value: string): void {
        this.layerChange.emit({ ...this.layer, style: { ...this.layer.style, [field]: value || null } });
    }

    emitComponentPosition(field: keyof PositionConfig, value: string): void {
        this.layerChange.emit({ ...this.layer, componentPosition: { ...this.layer.componentPosition, [field]: value || null } });
    }

    onFileChange(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0] ?? null;
        this.imageFileSelected.emit(file);
        (event.target as HTMLInputElement).value = '';
    }
}
